/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import { Image } from "@packages/framework";
import type { MarqueeProps } from "react-fast-marquee";
import Marquee from "react-fast-marquee";

import {
  constantSemanticColors,
  numericPixels,
  numericValues,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { emotionDynamics } from "../../../../lib/emotionDynamics";
import type { Color } from "../../../../types/color.emotion.types";
import { Text } from "../../../_base/_core/Text/Text.emotion";

const titleStyle: Interpolation = {
  bottom: numericValues[0],
  color: constantSemanticColors.onColorHighest,
  padding: spacing.s,
  [cssMediaQueryMinWidthTablet]: { padding: spacing.m },
  position: "absolute",
  textAlign: "center",
  width: spacing.full,
};
const boxStyle: Interpolation<{ background: string }> = (props) => ({
  background: props.background,
  marginRight: spacing.s,
  overflow: "hidden",
  position: "relative",
});
const imageStyle: Interpolation = {
  height: spacing.full,
  objectFit: "cover",
  width: spacing.full,
};

const boxSizeStyles: Record<ContentMarqueeProps["size"], Interpolation> = {
  s: {
    borderRadius: radius.s,
    height: spacing.xxl,
    width: numericPixels[72],
  },
  l: {
    borderRadius: radius.m,
    height: numericPixels[120],
    width: numericPixels[180],
    [cssMediaQueryMinWidthTablet]: {
      height: numericPixels[250],
      width: numericPixels[360],
    },
  },
};
const imageSizeStyles: Record<ContentMarqueeProps["size"], Interpolation> = {
  s: {
    height: spacing.full,
    width: spacing.full,
  },
  l: {
    height: numericPixels[106],
    width: spacing.full,
    [cssMediaQueryMinWidthTablet]: { height: numericPixels[226] },
  },
};

export type ContentMarqueeProps = MarqueeProps & {
  content: Array<{
    titleMessage: string;
    colorHex: Color;
    image: any;
  }>;
  maskImages?: boolean;
  maskMarquee?: boolean;
  size?: "s" | "l";
};

export const ContentMarquee = ({
  content,
  maskImages = true,
  maskMarquee = false,
  size = "l",
  ...props
}: ContentMarqueeProps) => (
  <Marquee direction={"left"} autoFill speed={25} {...props}>
    {content.map((content) => (
      <div
        key={`${content.titleMessage}-${content.colorHex}`}
        css={[boxStyle({ background: content.colorHex }), boxSizeStyles[size]]}
      >
        <div
          css={[
            imageSizeStyles[size],
            maskImages &&
              emotionDynamics.mask({
                directions: ["to top"],
                spreads: [56], // spacingMasks.s
              }),
          ]}
        >
          <Image
            src={content.image}
            alt={content.titleMessage}
            width={720}
            height={500}
            css={imageStyle}
          />
        </div>
        <Text type={"title"} size={"m"} as={"p"} css={titleStyle}>
          <I18nMessage message={content.titleMessage} />
        </Text>
      </div>
    ))}
  </Marquee>
);
